import {
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import SimpleAlert from "../../../components/display/SimpleAlert";
import { GET_ACTIVE_THERAPIES_LIST } from "../../../enums/ApiPaths";
import { GET_ALL_ACTIVE_THERAPIES_KEY } from "../../../enums/QueryKeys";
import { SINGLE_THERAPY_VIEW } from "../../../enums/UrlPaths";
import { useFetch } from "../../../hooks/Fetching";
import { TrimmedTableCell } from "../../appointments/singleView/components/VaccinationTable";

const columns = [
  { key: "boxName", label: "BoxTitle" },
  { key: "petName", label: "PetName" },
  { key: "startDate", label: "StartDate" },
  { key: "endDate", label: "EndDate" },
  { key: "title", label: "TherapyTitle" },
];

export default function ActiveTherapiesTable() {
  const { t } = useTranslation();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const { data, isLoading, error } = useFetch(
    GET_ACTIVE_THERAPIES_LIST,
    null,
    GET_ALL_ACTIVE_THERAPIES_KEY,
  );
  const navigate = useNavigate();

  function handleNavigate(id) {
    if (!id) return;
    navigate(SINGLE_THERAPY_VIEW + id);
  }

  if (isLoading) {
    return <LinearProgress />;
  } else if (error) {
    return (
      <SimpleAlert message={"ErrorFetchingBoxesList"} severity={"error"} />
    );
  }

  const visibleRows = data?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map(({ key, label }) => (
                <TableCell key={key} sx={{ fontWeight: "bold" }}>
                  {t(label)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.length > 0 ? (
              visibleRows.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  onClick={() => handleNavigate(row.id)}
                  sx={{ cursor: "pointer" }}
                >
                  {columns.map(({ key }) => (
                    <TrimmedTableCell key={key} value={row[key] || "N/A"} />
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns?.length}
                  align="center"
                  sx={{ borderBottom: "none", py: 4 }}
                >
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    fontStyle="italic"
                  >
                    {t("NoTherapiesYet")}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {data?.length > 0 && (
        <TablePagination
          component="div"
          count={data?.length}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage={t("RowsPerPage")}
          backIconButtonProps={{
            title: t("PreviousPage"),
            "aria-label": t("PreviousPage"),
          }}
          nextIconButtonProps={{
            title: t("NextPage"),
            "aria-label": t("NextPage"),
          }}
        />
      )}
    </Paper>
  );
}
